import { KmsTypePublishCategory, KmsTypeCategoryMembership } from "@kaltura/mediaspace-shared-types";
import { addBaseUrl, encodeEntryNameForURI, translate } from "@kaltura/mediaspace-shared-utils";
import { CategoryType } from "@kaltura/mediaspace-shared-types";

export enum EnrichmentType {
    label = "label",
    url = "url",
    all = "all",
}

export enum SaveType {
    save = "save",
    crossApplicationUnpublish = "crossApplicationUnpublish",
}

export const galleryLabel = (gallery: KmsTypePublishCategory) =>
    translate("%1 (Gallery ID: %2)", [gallery.name, gallery.id]);

export const channelLabel = (channel: KmsTypePublishCategory, isKAF?: boolean) => {
    if (!isKAF) {
        // keeping original format for KMS
        return translate("%1 (Channel ID: %2)", [channel.name, channel.id]);
    }
    // For KAF: display additional details about course and category IDs to distinguish between Shared, Created, and Not Created courses.
    // "Not Created" refers to a course that exists in the LMS but has not yet been created in the Kaltura system.

    const idStr = String(channel.id);

    // Shared repository channels do not have course/category IDs , Just return the channel name
    if (channel.membership === KmsTypeCategoryMembership.SHARED) {
        return channel.name;
    }

    // If id indicates not created yet, decode course id and show N/A for Category ID
    const decodedCourseId = extractCourseIdFromNotCreated(idStr);
    if (decodedCourseId) {
        return translate("%1 (Course ID: %2, Category ID: N/A)", [channel.name, decodedCourseId]);
    }

    const courseId = channel.fullName?.split(">")?.pop() || "N/A";
    // If courseId is missing, show N/A for Course ID
    return translate("%1 (Course ID: %2, Category ID: %3)", [channel.name, courseId, idStr || "N/A"]);
};

// Get the membership tooltip text for a category
export const getMembershipTooltip = (
    membership: KmsTypeCategoryMembership | undefined | null,
    categoryType: CategoryType.channel | CategoryType.gallery
): string => {
    if (!membership) {
        return "";
    }

    switch (membership) {
        case KmsTypeCategoryMembership.OPEN:
            return categoryType === CategoryType.channel
                ? translate(
                      "Open - All logged-in users can view and contribute content (contribution is not allowed for viewer-role users)."
                  )
                : translate(
                      "Open - All logged-in users can view and contribute content (contribution is not allowed for viewer-role users).<br/>In case guests are allowed to the site, they can also view content."
                  );

        case KmsTypeCategoryMembership.RESTRICTED:
            return categoryType === CategoryType.channel
                ? translate("Restricted - All logged-in users can view content, only channel members can contribute.")
                : translate(
                      "Restricted - All logged-in users can view content, only gallery members can contribute.<br/>In case guests are allowed to the site, they can also view content."
                  );

        case KmsTypeCategoryMembership.PRIVATE:
            return categoryType === CategoryType.channel
                ? translate("Private - Only channel members can view and contribute content.")
                : translate("Private - Only gallery members can view and contribute content.");

        case KmsTypeCategoryMembership.SHARED:
            return translate(
                "Shared Repository - Only members can view and contribute content. Content can be published to other locations based on entitlements."
            );

        case KmsTypeCategoryMembership.PUBLIC_RESTRICTED:
            return translate(
                "Public, Restricted - Anyone can view content (including guests). Only channel members can contribute according to their publishing entitlements."
            );

        case KmsTypeCategoryMembership.PUBLIC_OPEN:
            return translate(
                "Public, Open - Anyone can view content (including guests). All logged in users can contribute content (contribution is not allowed for viewer-role users)."
            );

        default:
            return "";
    }
};

// Generate combined tooltip including membership, moderation, and category label
export const getCombinedTooltipText = (
    category: KmsTypePublishCategory,
    categoryType: CategoryType.channel | CategoryType.gallery,
    nameLabel: string
): string => {
    const tooltips: string[] = [];

    tooltips.push(nameLabel);

    const membershipTooltip = getMembershipTooltip(category.membership, categoryType);
    if (membershipTooltip) {
        tooltips.push(membershipTooltip);
    }

    if (category.moderation) {
        tooltips.push(translate("Moderated - Content will only be available after moderator approval."));
    }

    return tooltips.join("\n\n").replace(/<br\s*\/?>/gi, "\n");
};

// Generates a URL for the given category based on its type
const getCategoryUrl = (category: KmsTypePublishCategory, categoryType: CategoryType) => {
    switch (categoryType) {
        case CategoryType.channel:
            return addBaseUrl(`/channel/${encodeEntryNameForURI(category.name)}/${category.id}`);
        case CategoryType.gallery:
            return addBaseUrl(`/category/${encodeEntryNameForURI(category.name)}/${category.id}`);
        case CategoryType.externalCategory:
            // external category will never have url
            return undefined;
        default:
            return undefined;
    }
};

function extractCourseIdFromNotCreated(id: string): string | null {
    const match = id.match(/^notcreatedyetcat_([^_]+)_/i);
    if (!match || !match[1]) return null;
    try {
        return atob(match[1]);
    }
    catch {
        return null;
    }
}

// Generates a label for the given category based on its type
const getCategoryLabel = (category: KmsTypePublishCategory, categoryType: CategoryType) => {
    switch (categoryType) {
        case CategoryType.channel:
            return channelLabel(category);
        case CategoryType.gallery:
            return galleryLabel(category);
        case CategoryType.externalCategory:
            return translate("Category ID: %1", [category.id]);
        default:
            return undefined;
    }
};

// Enriches the given categories with labels and/or URLs based on the enrichment type
export const enrichCategoriesData = (
    categories: KmsTypePublishCategory[],
    categoryType: CategoryType,
    enrichmentType: EnrichmentType
) => {
    const shouldEnrichWithLabel = enrichmentType === EnrichmentType.label || enrichmentType === EnrichmentType.all;
    const shouldEnrichWithUrl = enrichmentType === EnrichmentType.url || enrichmentType === EnrichmentType.all;
    return categories.map((category) => ({
        ...category,
        url: shouldEnrichWithUrl ? getCategoryUrl(category, categoryType) : undefined,
        label: shouldEnrichWithLabel ? getCategoryLabel(category, categoryType) : undefined,
    }));
};

// Enriches the given channels and galleries with labels and/or URLs and combines them into a single array
export const enrichAndCombineCategories = (
    channels: KmsTypePublishCategory[],
    galleries: KmsTypePublishCategory[],
    enrichmentType: EnrichmentType
) => {
    // enrich channel with label, url or both
    const channelLocations = enrichCategoriesData(channels, CategoryType.channel, enrichmentType);
    // enrich gallery with label, url or both
    const galleryLocations = enrichCategoriesData(galleries, CategoryType.gallery, enrichmentType);
    // concat the channels and galleries
    return [...galleryLocations, ...channelLocations];
};
